// src/components/MCPEdit.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
  Chip,
  Switch,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../services/api";

/** ===== Types ===== */
interface ParamMeta {
  type: string;
  required?: boolean;
  constraints?: Record<string, any>;
  default?: any;
  description: string;
  example?: any;
}

interface MCPFunction {
  func_key: string;
  display_name: string;
  description: string;
  method: string;
  tags: string[];
  input_schema: Record<string, ParamMeta>;
  output_schema: Record<string, ParamMeta>;
  additional_prompt: string;
}

interface ModuleDetail {
  module_key: string;
  description: string;
  endpoint_prefix: string;
  tags: string[];
  owner_user_id?: string | null;
  auto_approve?: boolean; // ✅ 자동 승인
}

/** ===== Component ===== */
export default function MCPEdit() {
  const { module_key } = useParams<{ module_key: string }>();
  const navigate = useNavigate();

  // server state
  const [module, setModule] = useState<ModuleDetail | null>(null);
  const [functions, setFunctions] = useState<MCPFunction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // 로딩 상태
  const [moduleLoading, setModuleLoading] = useState<boolean>(false);
  const [functionsLoading, setFunctionsLoading] = useState<boolean>(false);

  // JSON editor
  const [jsonText, setJsonText] = useState<string>("{");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [allowDeleteMissing, setAllowDeleteMissing] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 권한 표시용 (수정 가능 여부)
  const meUserId = localStorage.getItem("user_id") || "";
  const meTier = Number(localStorage.getItem("user_tier") || "1"); // 0=owner(관리자)
  const canEdit = useMemo(
    () => !!module && (module.owner_user_id === meUserId || meTier === 0),
    [module, meUserId, meTier]
  );

  const authHeaders = useMemo(
    () => ({
      "x-user-id": localStorage.getItem("user_id") || "",
      "x-access-token": localStorage.getItem("access_token") || "",
      "x-refresh-token": localStorage.getItem("refresh_token") || "",
    }),
    []
  );

  /** ===== Helpers ===== */
  const normalizeTags = (t: unknown): string[] => {
    if (Array.isArray(t)) return t.map(String);
    if (typeof t === "string") {
      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch {
        // 콤마 분리 등의 문자열은 안전상 무시 (에디터 모드라 사용자가 직접 JSON을 만짐)
        return [];
      }
    }
    return [];
  };

  const makeSnapshotFrom = (mod: ModuleDetail | null, fns: MCPFunction[]) => ({
    module: {
      module_key,
      description: mod?.description ?? "",
      endpoint_prefix: mod?.endpoint_prefix ?? `/mcp/${module_key}`,
      tags: normalizeTags(mod?.tags),
      auto_approve: !!mod?.auto_approve, // ✅ 자동 승인 스냅샷 반영
    },
    functions: fns.map((f) => ({
      func_key: f.func_key,
      display_name: f.display_name,
      description: f.description,
      method: f.method,
      tags: f.tags || [],
      input_schema: f.input_schema || {},
      output_schema: f.output_schema || {},
      additional_prompt: f.additional_prompt || "",
    })),
  });

  /** ===== Fetchers ===== */
  const fetchModule = async () => {
    if (!module_key) return;
    setModuleLoading(true);
    try {
      const res = await axios.get(`${API_BASE}mcp-modules/${module_key}`, { headers: authHeaders });
      if (res.data?.success) {
        // 서버는 { data: { module: {...} } } 형태
        const mod: ModuleDetail = res.data.data?.module;
        if (!mod) throw new Error("모듈 정보를 찾을 수 없습니다.");
        // auto_approve 누락 시 false로
        setModule({
          ...mod,
          auto_approve: typeof (mod as any).auto_approve === "boolean" ? (mod as any).auto_approve : false,
        });
      } else {
        throw new Error(res.data?.message || "모듈 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "모듈 정보를 불러오는 중 오류 발생");
    } finally {
      setModuleLoading(false);
    }
  };

  const fetchFunctions = async () => {
    if (!module_key) return;
    setFunctionsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}mcp-functions/${module_key}`, { headers: authHeaders });
      if (res.data?.success) {
        setFunctions(res.data.data?.functions || []);
      } else {
        throw new Error(res.data?.message || "함수 목록 조회 실패");
      }
    } catch (e: any) {
      setError(e?.message || "함수 목록을 불러오는 중 오류 발생");
    } finally {
      setFunctionsLoading(false);
    }
  };

  /** 초기 로딩: state 업데이트 이후 JSON 자동 갱신(useEffect로) */
  useEffect(() => {
    if (!module_key) return;
    (async () => {
      setError(null);
      await Promise.all([fetchModule(), fetchFunctions()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module_key]);

  /** module/functions 변경 시 JSON 스냅샷 자동 갱신 */
  useEffect(() => {
    if (!module) return;
    const snap = makeSnapshotFrom(module, functions);
    setJsonText(JSON.stringify(snap, null, 2));
    setJsonError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, functions]);

  /** ===== Import/Export ===== */
  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = JSON.parse(String(reader.result));
        setJsonText(JSON.stringify(imported, null, 2));
        setJsonError(null);
        setInfo("JSON 불러오기 완료. 저장 버튼을 눌러 서버에 반영하세요.");
      } catch (err: any) {
        setJsonError("Import 실패 – JSON 파싱 오류: " + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleExport = () => {
    const blob = new Blob([jsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${module_key}_module_and_functions.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /** ===== Save (Apply JSON to Server) ===== */
  const parseJsonStrict = (): { module: ModuleDetail; functions: MCPFunction[] } => {
    const obj = JSON.parse(jsonText || "{}");
    if (!obj || typeof obj !== "object") throw new Error("루트 JSON은 객체여야 합니다.");
    const m = obj.module || {};
    if (!m || typeof m !== "object") throw new Error("module 필드가 필요합니다.");

    // basic module validation
    const mk = String(m.module_key ?? module_key ?? "");
    if (!mk || String(mk) !== String(module_key)) {
      throw new Error(`module.module_key 가 현재 URL의 module_key(${module_key})와 일치해야 합니다.`);
    }

    // auto_approve는 boolean 강제
    const autoApprove = Boolean(m.auto_approve);

    const moduleClean: ModuleDetail = {
      module_key: mk,
      description: String(m.description ?? ""),
      endpoint_prefix: String(m.endpoint_prefix ?? `/mcp/${mk}`),
      tags: normalizeTags(m.tags),
      owner_user_id: module?.owner_user_id ?? null,
      auto_approve: autoApprove, // ✅ 포함
    };

    const fnsRaw = Array.isArray(obj.functions) ? obj.functions : [];
    const functionsClean: MCPFunction[] = fnsRaw.map((f: any) => ({
      func_key: String(f.func_key || ""),
      display_name: String(f.display_name || ""),
      description: String(f.description || ""),
      method: String(f.method || "POST").toUpperCase(),
      tags: Array.isArray(f.tags) ? f.tags.map((t: any) => String(t)) : [],
      input_schema:
        typeof f.input_schema === "object" && !Array.isArray(f.input_schema) ? f.input_schema : {},
      output_schema:
        typeof f.output_schema === "object" && !Array.isArray(f.output_schema) ? f.output_schema : {},
      additional_prompt: String(f.additional_prompt || ""),
    }));

    // quick checks
    functionsClean.forEach((f) => {
      if (!f.func_key) throw new Error("functions[].func_key 는 필수입니다.");
      if (!/^[A-Za-z0-9_\-]+$/.test(f.func_key))
        throw new Error(`func_key '${f.func_key}' 형식 오류 (영문/숫자/언더스코어/하이픈)`);
      if (!f.display_name) throw new Error(`'${f.func_key}' 의 display_name 은 필수입니다.`);
    });

    return { module: moduleClean, functions: functionsClean };
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setInfo(null);
    setError(null);

    try {
      const parsed = parseJsonStrict();

      // 저장 중 표시를 위해 functionsLoading 바 사용 (간단 공용 로딩)
      setFunctionsLoading(true);

      // 1) Update module meta (description/endpoint_prefix/tags/auto_approve)
      await axios.put(
        `${API_BASE}mcp-modules/${module_key}`,
        {
          description: parsed.module.description,
          endpoint_prefix: parsed.module.endpoint_prefix,
          tags: parsed.module.tags,
          auto_approve: parsed.module.auto_approve, // ✅ 전달
        },
        { headers: authHeaders }
      );

      // 2) Diff functions (create/update/delete)
      const currentMap = new Map(functions.map((f) => [f.func_key.toLowerCase(), f]));
      const nextMap = new Map(parsed.functions.map((f) => [f.func_key.toLowerCase(), f]));

      // 2-1) create or update
      for (const f of parsed.functions) {
        const exists = currentMap.get(f.func_key.toLowerCase());
        const payload = {
          func_key: f.func_key,
          display_name: f.display_name,
          description: f.description,
          method: f.method,
          tags: f.tags,
          input_schema: f.input_schema,
          output_schema: f.output_schema,
          additional_prompt: f.additional_prompt,
        };
        if (!exists) {
          await axios.post(`${API_BASE}mcp-functions/${module_key}`, payload, { headers: authHeaders });
        } else {
          await axios.put(
            `${API_BASE}mcp-functions/${module_key}/${encodeURIComponent(exists.func_key)}`,
            payload,
            { headers: authHeaders }
          );
        }
      }

      // 2-2) delete missing (optional)
      if (allowDeleteMissing) {
        const deletions: Promise<any>[] = [];
        currentMap.forEach((cur, k) => {
          if (!nextMap.has(k)) {
            deletions.push(
              axios.delete(
                `${API_BASE}mcp-functions/${module_key}/${encodeURIComponent(cur.func_key)}`,
                { headers: authHeaders }
              )
            );
          }
        });
        await Promise.all(deletions);
      }

      // refetch (자동 스냅샷은 module/functions useEffect가 처리)
      await Promise.all([fetchModule(), fetchFunctions()]);
      setInfo("저장 완료 — 서버 반영이 끝났습니다.");
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "저장 중 오류가 발생했습니다.";
      setError(msg);
    } finally {
      setFunctionsLoading(false);
    }
  };

  /** ===== Render ===== */
  if (error && !module) {
    return <Typography color="error">{error}</Typography>;
  }

  // 초기 모듈 로딩(아직 module이 없음) - 풀스크린 느낌의 로딩
  if (moduleLoading && !module) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress />
        <Typography color="grey.500">모듈 정보를 불러오는 중...</Typography>
      </Box>
    );
  }

  if (!module) {
    return <Typography color="grey.500">로딩 중...</Typography>;
  }

  // 토글이 JSON에도 즉시 반영되도록 헬퍼
  const setAutoApproveInJson = (checked: boolean) => {
    try {
      const obj = JSON.parse(jsonText || "{}");
      if (!obj.module || typeof obj.module !== "object") obj.module = {};
      obj.module.auto_approve = checked;
      const next = JSON.stringify(obj, null, 2);
      setJsonText(next);
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e?.message || "JSON 업데이트 오류");
    }
  };

  const autoApprove = !!module.auto_approve;

  return (
    <Box sx={{ p: 2 }}>
      {/* 상단 글로벌 로딩 배너: 모듈/함수 어느 하나라도 로딩 중이면 표시 */}
      {(moduleLoading || functionsLoading) && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>
              {moduleLoading
                ? "모듈 정보를 불러오는 중..."
                : functionsLoading
                ? "함수 목록/저장 작업을 처리하는 중..."
                : "처리 중..."}
            </Typography>
          </Box>
          <LinearProgress />
        </Paper>
      )}

      {/* 상단 바 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button onClick={() => navigate(-1)}>← 뒤로</Button>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleImportClick} disabled={moduleLoading || functionsLoading}>
            Import
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
          <Button variant="outlined" onClick={handleExport} disabled={moduleLoading || functionsLoading}>
            Export
          </Button>
        </Stack>
      </Box>

      {/* 안내 */}
      <Alert severity="info" sx={{ mb: 2 }}>
        이 화면은 <b>JSON 전용 편집 모드</b>입니다. 아래 JSON을 수정한 뒤 <b>저장</b>을 누르면
        <b> 모듈 메타(설명/엔드포인트/태그/자동 승인)</b>와 <b>함수 목록</b>이 서버에 반영됩니다.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              MCP 모듈: {module.module_key}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                소유자: <b>{module.owner_user_id || "—"}</b> · 엔드포인트: <b>{module.endpoint_prefix}</b>
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={autoApprove ? "자동 승인 ON" : "자동 승인 OFF"}
                color={autoApprove ? "success" : "default"}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* ✅ 자동 승인 토글 (JSON과 동기화) */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoApprove}
                  onChange={(e) => {
                    const next = e.target.checked;
                    // 화면 상태 반영
                    setModule((m) => (m ? { ...m, auto_approve: next } : m));
                    // JSON에 즉시 반영
                    setAutoApproveInJson(next);
                  }}
                  disabled={!canEdit || moduleLoading || functionsLoading}
                />
              }
              label="자동 승인"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={allowDeleteMissing}
                  onChange={(e) => setAllowDeleteMissing(e.target.checked)}
                  disabled={!canEdit || moduleLoading || functionsLoading}
                />
              }
              label="JSON에 없는 함수는 삭제"
            />
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canEdit || !!jsonError || moduleLoading || functionsLoading}
            >
              {functionsLoading ? "저장 중..." : "저장"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {info && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={28}
        value={jsonText}
        onChange={(e) => {
          setJsonText(e.target.value);
          try {
            JSON.parse(e.target.value);
            setJsonError(null);
          } catch (err: any) {
            setJsonError(err.message);
          }
        }}
        sx={{ fontFamily: "Consolas, Menlo, Monaco, 'Courier New', monospace" }}
        disabled={moduleLoading || functionsLoading}
      />
      {jsonError && (
        <Typography color="error" sx={{ mt: 1 }}>
          JSON 파싱 오류: {jsonError}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Alert severity="warning">
        JSON 예시 스키마
        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
{`{
  "module": {
    "module_key": "${module_key}",
    "description": "모듈 설명",
    "endpoint_prefix": "/mcp/${module_key}",
    "tags": ["qna", "document", "summary"],
    "auto_approve": false
  },
  "functions": [
    {
      "func_key": "qna",
      "display_name": "Q&A",
      "description": "문서 기반 질의응답",
      "method": "POST",
      "tags": ["qna", "document"],
      "input_schema": {
        "question": {"type": "string", "required": true, "description": "질문"}
      },
      "output_schema": {
        "answer": {"type": "string", "description": "답변"}
      },
      "additional_prompt": ""
    }
  ]
}`}
        </pre>
      </Alert>
    </Box>
  );
}
